import React, { useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { GetStaticPropsContext } from "next";
import { ThemeProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import {
  QueryClientProvider,
  Hydrate,
  QueryClient,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NextIntlProvider } from "next-intl";
import FontLoader from "shared/components/font-loader";
import MainLayout from "shared/components/layout";
import theme from "styles/theme/theme";
import "../styles/globals.css";
import "../styles/helper-spacing.css";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import { store } from "store";
import NewsContext from "shared/context/news.context";
import LayoutProtect from "@components/enviroment-protect/LayoutProtect";
import { en, es } from "@lang";
import createCanonicalUrl from "@helpers/removeparams";
import "react-photo-view/dist/react-photo-view.css";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { ModalProvider } from "shared/context/ModalAlert.context";

export async function getStaticProps({
  locale,
  preview,
}: GetStaticPropsContext) {
  return {
    props: {
      messages: locale === "es" ? es : en,
    },
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function storePathValues() {
  const storage = globalThis?.sessionStorage;
  if (!storage) return;

  if (storage.getItem("currentPath") != globalThis.location.pathname) {
    const prevPath = storage.getItem("currentPath");
    storage.setItem("prevPath", prevPath || "");
  }
  storage.setItem("currentPath", globalThis.location.pathname);
}

import type { NextRouter } from "next/router";

function handleIdiomLoader(router: NextRouter) {
  const loaderIdiom = localStorage.getItem("idiom");
  if (loaderIdiom && loaderIdiom !== router.locale) {
    router
      .replace(router.asPath, router.asPath, {
        locale: loaderIdiom.toLowerCase(),
      })
      .catch((err) => {
        // Ignore the "Cancel rendering route" error thrown when the navigation is interrupted.
        if (err?.cancelled) return;
        console.error("[handleIdiomLoader] Failed to change locale", err);
      });
  }
}

function MyApp(props: any) {
  const { Component, pageProps } = props;
  const router = useRouter();
  const Layout = Component.Layout || React.Fragment;
  const locate = router.locale === "es" ? es : en;

  useEffect(() => {
    storePathValues();
  }, [router.asPath]);

  useEffect(() => {
    handleIdiomLoader(router);
  }, []);

  const appContent = (
    <>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
      >
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          />
          <link rel="canonical" href={createCanonicalUrl(router.asPath)} />
        </Head>

        <FontLoader />

        <NextIntlProvider messages={locate}>
          <ThemeProvider theme={theme}>
            <ModalProvider>
              <SnackbarProvider maxSnack={3}>
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                  <Hydrate state={pageProps.dehydratedState}>
                    <Provider store={store}>
                      <NewsContext>
                        <MainLayout>
                          <Layout>
                            <Component {...pageProps} />
                          </Layout>
                        </MainLayout>
                      </NewsContext>
                    </Provider>
                  </Hydrate>
                  <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
              </SnackbarProvider>
            </ModalProvider>
          </ThemeProvider>
        </NextIntlProvider>
      </GoogleReCaptchaProvider>
    </>
  );

  return <LayoutProtect>{appContent}</LayoutProtect>;
}

export default MyApp;
